import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "react-bootstrap";
import Slider from "react-slick";
import { getSliders, getUploadUrl } from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";
import { useLanguage, pickLang } from "../../contexts/LanguageContext";
import "./HeroSlider.css";

const HeroSlider = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileImageModes, setMobileImageModes] = useState({});
  const { language } = useLanguage();

  useEffect(() => {
    fetchSliders();
  }, []);

  const fetchSliders = async () => {
    try {
      const response = await getSliders();
      if (response.data.success) {
        setSlides(response.data.data);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching sliders:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!slides.length) return;

    let isCancelled = false;

    const detectMobileImageModes = async () => {
      const checks = slides.map((slide) => {
        if (!slide.foto) {
          return Promise.resolve({ id: slide.id, mode: "cover" });
        }

        const imageUrl = getUploadUrl(slide.foto);
        return new Promise((resolve) => {
          const img = new Image();

          img.onload = () => {
            // Landscape banner can safely fill mobile area; others are better fully visible.
            const ratio = img.naturalWidth / img.naturalHeight;
            const mode = ratio >= 1.55 ? "cover" : "contain";
            resolve({ id: slide.id, mode });
          };

          img.onerror = () => {
            resolve({ id: slide.id, mode: "cover" });
          };

          img.src = imageUrl;
        });
      });

      const results = await Promise.all(checks);
      if (isCancelled) return;

      const nextModes = {};
      results.forEach(({ id, mode }) => {
        nextModes[id] = mode;
      });
      setMobileImageModes(nextModes);
    };

    detectMobileImageModes();

    return () => {
      isCancelled = true;
    };
  }, [slides]);

  // Custom arrow components
  const NextArrow = ({ onClick }) => {
    return (
      <button
        className="slick-arrow slick-next"
        onClick={onClick}
        aria-label="Next slide"
      >
        <i className="fas fa-chevron-right"></i>
      </button>
    );
  };

  const PrevArrow = ({ onClick }) => {
    return (
      <button
        className="slick-arrow slick-prev"
        onClick={onClick}
        aria-label="Previous slide"
      >
        <i className="fas fa-chevron-left"></i>
      </button>
    );
  };

  const settings = {
    dots: true,
    infinite: slides.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: slides.length > 1,
    autoplaySpeed: 5000,
    fade: true,
    cssEase: "linear",
    arrows: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    pauseOnHover: true,
    adaptiveHeight: false,
    dotsClass: "slick-dots",
  };

  const { orgName } = useTheme();

  const slideHasText = (slide) => {
    const judul = (pickLang(slide, "judul", language) || "").trim();
    const singkat = (
      pickLang(slide, "deskripsisingkat", language) || ""
    ).trim();
    const deskripsi = (pickLang(slide, "deskripsi", language) || "").trim();
    return !!(judul || singkat || deskripsi);
  };

  if (loading) {
    return (
      <div className="slider-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (slides.length === 0) {
    return (
      <div className="hero-slider default-slide">
        <div className="slide-content">
          <div className="container">
            <h1>
              {orgName} -{" "}
              {language === "en"
                ? "Halal Education Indonesia"
                : "Edukasi Halal Indonesia"}
            </h1>
            <p>
              {language === "en"
                ? "Indonesian Halal Product Supervisory and Oversight Institution"
                : "Lembaga Penyelia dan Pengawas Produk Halal Indonesia"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-slider">
      <Slider {...settings}>
        {slides.map((slide) => (
          <div key={slide.id}>
            <div
              className={`slide-item ${
                mobileImageModes[slide.id] === "contain"
                  ? "mobile-bg-contain"
                  : "mobile-bg-cover"
              }`}
              style={{
                backgroundImage: slide.foto
                  ? `url(${getUploadUrl(slide.foto)})`
                  : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              }}
            >
              {slideHasText(slide) && <div className="slide-overlay"></div>}
              <div className="slide-content">
                <div className="container">
                  <h1>{pickLang(slide, "judul", language)}</h1>
                  {pickLang(slide, "deskripsisingkat", language) && (
                    <p className="lead">
                      {pickLang(slide, "deskripsisingkat", language)}
                    </p>
                  )}
                  {pickLang(slide, "deskripsi", language) && (
                    <div
                      className="slide-description"
                      dangerouslySetInnerHTML={{
                        __html: pickLang(slide, "deskripsi", language),
                      }}
                    />
                  )}
                  {slide.teks_tombol && slide.link && (
                    <div className="slide-button-container">
                      {slide.link.startsWith("http") ||
                      slide.link.startsWith("https") ? (
                        <Button
                          as="a"
                          href={slide.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="light"
                          size="lg"
                          className="slide-button"
                        >
                          {pickLang(slide, "teks_tombol", language)}
                        </Button>
                      ) : (
                        <Button
                          as={Link}
                          to={slide.link}
                          variant="light"
                          size="lg"
                          className="slide-button"
                        >
                          {pickLang(slide, "teks_tombol", language)}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </Slider>
    </div>
  );
};

export default HeroSlider;
